import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AuthState, LoginResult } from '@rsmaids/shared/types/auth';
import { AuthContext, defaultAuthState } from './auth-context-core';
import { signOut, onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { collection, doc, query, where, orderBy, limit, onSnapshot, Timestamp, Unsubscribe } from 'firebase/firestore';
import { auth, dbRsRoles } from '../auth/firebase';
import { rsAuthClient } from '@rsmaids/shared/services/rsAuthClient';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { getFCMToken, storeDeviceTokenViaFunction, requestNotificationPermission } from '../utils/notificationUtils';
import { registerTwilioVoip } from '../utils/registerTwilioVoip';
import { PHONE_ROLES, type Role } from '@rsmaids/shared/constants/roles';
import { warmAllFunctions } from '../services/helcimInvoicingService';
import { startContactsSync, stopContactsSync, clearContactsCache } from '../services/contactsSyncService';
import type { Notification } from '../hooks/useNotifications';
import type { CategoryCounts } from './auth-context-core';
import { useTranslation } from 'react-i18next';


// The provider component that will wrap your application
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [authState, setAuthState] = useState<AuthState>(defaultAuthState);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  useEffect(() => {
    if (authState.isLoading) return;

    let secondFrame = 0;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        window.dispatchEvent(new Event('rsmaids-app-ready'));
      });
    });

    return () => {
      cancelAnimationFrame(firstFrame);
      if (secondFrame) cancelAnimationFrame(secondFrame);
    };
  }, [authState.isLoading]);

  // The badge counts unread notifications that arrived since the alerts were last
  // opened — worked out from the newest notifications already loaded below, so
  // it costs no extra Firestore reads. (It used to be a second listener over
  // EVERY unread notification just to count them, re-reading all ~1,400 on each
  // cold start.) Until the alerts have been opened once, "last opened" is 0, so
  // everything unread counts. (Key is _v2: an earlier build wrongly stamped
  // "now" on first launch, which silently cleared the badge.)
  // Per user: the "last opened" time is stored under the logged-in user's id and
  // loaded when auth confirms who that is (below), so two people on one device
  // don't share it.
  const seenKeyFor = (uid: string) => `notificationsLastSeenAt_v2_${uid}`;
  const [notificationsSeenAt, setNotificationsSeenAt] = useState<number>(0);
  const seenUidRef = useRef<string | null>(null);
  const markNotificationsSeen = useCallback(() => {
    const now = Date.now();
    const uid = seenUidRef.current;
    if (uid) {
      try { localStorage.setItem(seenKeyFor(uid), String(now)); } catch { /* non-critical */ }
    }
    setNotificationsSeenAt(now);
  }, []);
  const unreadCount = useMemo(() => {
    const fresh = notifications.filter((n) => {
      if (n.read) return false;
      const created = n.createdAt instanceof Timestamp ? n.createdAt.toMillis() : new Date(n.createdAt as string).getTime();
      return created > notificationsSeenAt;
    }).length;
    // Only the newest 50 are loaded. If every one of them is new there are
    // likely more, so report 100 and let the badges show "99+".
    return notifications.length >= 50 && fresh === notifications.length ? 100 : fresh;
  }, [notifications, notificationsSeenAt]);
  // The phone's app-icon badge follows the same number as the in-app badge (and
  // clears on logout, when it drops to 0). This is the only place that sets it.
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        reg.active?.postMessage({ type: 'UPDATE_BADGE', badgeCount: unreadCount });
      }).catch(() => { /* no service worker — nothing to update */ });
    }
  }, [unreadCount]);
  // Per-category counts of the unread notifications that are loaded (the newest 50).
  const categoryCounts = useMemo<CategoryCounts>(() => {
    const counts: CategoryCounts = { booking: 0, payment: 0, team: 0, form: 0, overdue: 0, autoInvoice: 0 };
    notifications.forEach((n) => {
      if (n.read) return;
      const data = n as unknown as { category?: string; message?: string; title?: string };
      const cat = data.category || '';
      if (cat === 'booking') counts.booking++;
      else if (cat === 'payment') counts.payment++;
      else if (cat === 'team') counts.team++;
      else if (cat === 'form') counts.form++;
      const msg = (data.message || '').toLowerCase();
      const title = (data.title || '').toLowerCase();
      if (msg.includes('overdue') || title.includes('overdue')) counts.overdue++;
      if (msg.includes('invoice sent') || msg.includes('automatic invoice')) counts.autoInvoice++;
    });
    return counts;
  }, [notifications]);
  const notificationsUnsubscribeRef = useRef<Unsubscribe | null>(null);
  const preferencesUnsubscribeRef = useRef<Unsubscribe | null>(null);
  const authHydrationInFlightRef = useRef(false);

  // Handle app coming back to foreground - refresh token to ensure it's still valid
  // Skip refresh for short background trips (e.g. camera) to avoid crashing Samsung Internet
  useEffect(() => {
    let backgroundedAt = 0;
    const handleVisibilityChange = async () => {
      if (document.hidden) {
        backgroundedAt = Date.now();
        console.log('📱 [Visibility] App backgrounded');
        return;
      }
      
      const bgDuration = backgroundedAt ? Date.now() - backgroundedAt : 0;
      console.log(`📱 [Visibility] App came back to foreground after ${Math.round(bgDuration / 1000)}s`);
      
      // Only refresh token if app was in background for more than 2 minutes
      // Short trips (camera, file picker) don't need a token refresh and the
      // heavy Firebase Messaging + network calls can crash Samsung Internet
      if (bgDuration < 120000) {
        console.log('📱 [Visibility] Short background trip — skipping token refresh');
        return;
      }

      // If user is logged in, trigger a token refresh
      // This ensures tokens don't expire while in background
      if (authState.isLoggedIn && authState.user?.id) {
        console.log('📱 [Visibility] Triggering token refresh');
        // Dispatch custom event to trigger token refresh in the other useEffect
        window.dispatchEvent(new CustomEvent('fcm-token-refresh-needed', {
          detail: { reason: 'App returned to foreground' }
        }));
        // Re-register Twilio VoIP so FCM push binding stays current
        if (PHONE_ROLES.includes(authState.user.role as Role)) registerTwilioVoip().catch(() => {});
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [authState.isLoggedIn, authState.user?.id, authState.user?.role]);

  // Start notifications listener as soon as Firebase Auth confirms the user —
  // before any backend calls, so data is already loading while auth resolves.
  useEffect(() => {
    const t0 = performance.now();
    console.log(`[NOTIF T0] useEffect mounted at ${t0.toFixed(0)}ms`);

    const unsubscribe = onAuthStateChanged(auth, (_firebaseUser: FirebaseUser | null) => {
      const t1 = performance.now();
      console.log(`[NOTIF T1] onAuthStateChanged fired at ${t1.toFixed(0)}ms (+${(t1 - t0).toFixed(0)}ms from mount), user=${_firebaseUser?.uid ?? 'null'}`);

      // Tear down previous listeners if user changed or logged out
      if (notificationsUnsubscribeRef.current) {
        notificationsUnsubscribeRef.current();
        notificationsUnsubscribeRef.current = null;
      }
      if (preferencesUnsubscribeRef.current) {
        preferencesUnsubscribeRef.current();
        preferencesUnsubscribeRef.current = null;
      }
      if (!_firebaseUser) {
        seenUidRef.current = null;
        setNotificationsSeenAt(0);
        setNotifications([]);
        stopContactsSync();
        // Signed out: nothing about customers stays saved on this device for the next person to sign in
        clearContactsCache();
        return;
      }
      startContactsSync();
      const uid = _firebaseUser.uid;
      seenUidRef.current = uid;
      try {
        setNotificationsSeenAt(Number(localStorage.getItem(seenKeyFor(uid))) || 0);
      } catch {
        setNotificationsSeenAt(0);
      }

      // The Firebase SDK does NOT automatically reconnect onSnapshot listeners when
      // a connection-level error (e.g. CORS on named-database session reset) kills
      // the stream. Each factory function resubscribes itself after 5 s, but only
      // if the same Firebase user is still signed in.

      const subscribeToPreferences = () => {
        if (auth.currentUser?.uid !== uid) return;
        preferencesUnsubscribeRef.current = onSnapshot(
          doc(dbRsRoles, 'Roles', uid),
          (userDoc) => {
            if (!userDoc.exists()) return;
            const data = userDoc.data();
            if (!data.preferences) return;
            setAuthState(prev => {
              if (!prev.user || !prev.isLoggedIn) return prev;
              const updatedUser = { ...prev.user, preferences: data.preferences };
              try {
                const sd = localStorage.getItem('session');
                if (sd) {
                  const parsed = JSON.parse(sd);
                  parsed.user = updatedUser;
                  localStorage.setItem('session', JSON.stringify(parsed));
                }
              } catch { /* non-critical */ }
              return { ...prev, user: updatedUser };
            });
          },
          (err) => {
            console.warn('[AuthContext] Preferences listener error, resubscribing in 5s:', err);
            setTimeout(subscribeToPreferences, 5000);
          }
        );
      };
      subscribeToPreferences();

      const q = query(
        collection(dbRsRoles, 'notifications'),
        where('recipientId', '==', uid),
        orderBy('createdAt', 'desc'),
        limit(50)
      );

      const t2 = performance.now();
      console.log(`[NOTIF T2] onSnapshot query created at ${t2.toFixed(0)}ms (+${(t2 - t1).toFixed(0)}ms)`);

      let firstSnapshot = true;
      const subscribeToNotifications = () => {
        if (auth.currentUser?.uid !== uid) return;
        notificationsUnsubscribeRef.current = onSnapshot(q, (snapshot) => {
          const t3 = performance.now();
          if (firstSnapshot) {
            firstSnapshot = false;
            console.log(`[NOTIF T3] FIRST snapshot received at ${t3.toFixed(0)}ms (+${(t3 - t2).toFixed(0)}ms from query, +${(t3 - t0).toFixed(0)}ms total) | fromCache=${snapshot.metadata.fromCache} | docs=${snapshot.docs.length}`);
          } else {
            console.log(`[NOTIF T3] Subsequent snapshot at ${t3.toFixed(0)}ms | fromCache=${snapshot.metadata.fromCache} | docs=${snapshot.docs.length}`);
          }
          const notifs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Notification[];
          setNotifications(notifs);
        }, (err) => {
          console.error('[AuthContext] Notifications listener error, resubscribing in 5s:', err);
          setTimeout(subscribeToNotifications, 5000);
        });
      };
      subscribeToNotifications();
    });
    return () => {
      unsubscribe();
      stopContactsSync();
      if (notificationsUnsubscribeRef.current) {
        notificationsUnsubscribeRef.current();
      }
      if (preferencesUnsubscribeRef.current) {
        preferencesUnsubscribeRef.current();
      }
    };
  }, []);

  // Effect 1: Initialize auth state on app startup (runs once)
  useEffect(() => {
    console.log('[AuthContext] App starting - initializing auth state');
    let isMounted = true;

    (async () => {
      try {
        const authService = DataServiceFactory.getService().auth;
        const state = await authService.verifyLogin();
        
        if (!isMounted) return;
        
        let enhancedUser = state.user;
        
        // First priority: Check for stored contact profile data in localStorage (from previous login)
        if (state.isLoggedIn && state.user) {
          try {
            const storedProfile = localStorage.getItem('contactProfile') || localStorage.getItem('wixContactProfile');
            if (storedProfile) {
              const parsedProfile = JSON.parse(storedProfile);
              const contactProfileData = parsedProfile.profile ? parsedProfile : { profile: parsedProfile };

              enhancedUser = {
                ...state.user,
                // Use display name if available, otherwise nickname, otherwise keep existing name
                name: contactProfileData.profile?.displayName || contactProfileData.profile?.nickname || state.user.name,
                // Extract first name from display name or nickname for firstName field
                firstName: (contactProfileData.profile?.displayName || contactProfileData.profile?.nickname || state.user.name)?.split(' ')[0] || state.user.firstName,
                // Store avatar photo if available
                ...(contactProfileData.profile?.avatar && { 
                  photoURL: contactProfileData.profile.avatar,
                  avatarUrl: contactProfileData.profile.avatar,
                  profileImage: contactProfileData.profile.avatar
                }),
                // Store profile data in the existing profileData field
                profileData: {
                  ...state.user.profileData,
                  contactId: contactProfileData.contact?._id || contactProfileData._id,
                  loginEmail: contactProfileData.contact?.loginEmail || contactProfileData.loginEmail,
                  profile: contactProfileData.profile
                }
              };
            }
          } catch (storageError) {
            console.warn('[AuthContext] Error reading stored contact profile during auth restoration:', storageError);
          }
        }
        
        console.log('[AuthContext] Auth initialization complete. Setting state:', { 
          isLoggedIn: state.isLoggedIn, 
          hasUser: !!enhancedUser,
          userEmail: enhancedUser?.email 
        });
        
        setAuthState({
          isLoggedIn: state.isLoggedIn,
          isLoading: false,
          user: enhancedUser || null,
          error: null
        });

        // If the restored session has no preferences, silently refresh from backend
        if (state.isLoggedIn && enhancedUser && !enhancedUser.preferences) {
          try {
            const { getAuth } = await import('firebase/auth');
            const firebaseUser = getAuth().currentUser;
            if (firebaseUser) {
              const idToken = await firebaseUser.getIdToken();
              const authClient = new rsAuthClient();
              const fresh = await authClient.login(idToken);
              if (fresh.success && fresh.user && isMounted) {
                const freshUser = { ...enhancedUser, preferences: fresh.user.preferences };
                setAuthState({ isLoggedIn: true, isLoading: false, user: freshUser, error: null });
                try {
                  const sd = localStorage.getItem('session');
                  if (sd) {
                    const parsed = JSON.parse(sd);
                    parsed.user = freshUser;
                    localStorage.setItem('session', JSON.stringify(parsed));
                  }
                } catch {}
              }
            }
          } catch {
            // Non-critical — preferences will load on next login
          }
        }
      } catch (error) {
        console.error('[AuthContext] Auth initialization error:', error);
        if (isMounted) {
          setAuthState({
            isLoggedIn: false,
            isLoading: false,
            user: null,
            error: error instanceof Error ? error.message : t('contexts.AuthContext.unknownError')
          });
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [t]);

  // Effect 2: Listen for Firebase auth state changes (logout/expiration detection)
  // This effect runs AFTER initialization to detect when user logs out or session expires
  useEffect(() => {
    console.log('[AuthContext] Setting up Firebase auth state listener');
    const unsubscribe = onAuthStateChanged(auth, async (_firebaseUser: FirebaseUser | null) => {
      console.log('[AuthContext] Firebase auth state changed. User:', _firebaseUser?.uid);
      
      // If Firebase reports no user, clear auth state (user logged out)
      if (!_firebaseUser) {
        console.log('[AuthContext] Firebase user cleared - user logged out');
        setAuthState({
          isLoggedIn: false,
          isLoading: false,
          user: null,
          error: null
        });
        return;
      }

      if (authHydrationInFlightRef.current) {
        return;
      }

      authHydrationInFlightRef.current = true;

      try {
        const authService = DataServiceFactory.getService().auth;
        const state = await authService.verifyLogin();

        if (state.isLoggedIn && state.user) {
          const restoredUser = state.user;

          setAuthState(prev => {
            if (prev.isLoggedIn && prev.user?.id === restoredUser.id) {
              return prev;
            }

            return {
              isLoggedIn: true,
              isLoading: false,
              user: restoredUser,
              error: null
            };
          });
        }
      } catch (error) {
        console.warn('[AuthContext] Failed to hydrate auth state from Firebase session:', error);
      } finally {
        authHydrationInFlightRef.current = false;
      }
    });
    return () => unsubscribe();
  }, []);

  // Effect 3: Capture FCM token when auth state indicates logged in
  useEffect(() => {
    if (!authState.isLoggedIn || !authState.user?.id) return;

    (async () => {
      try {
        console.log('📱 [Auth Change] Capturing FCM token...');
        
        await requestNotificationPermission({ prompt: false }).catch(() => {
          // Silently continue if permission fails
        });
        
        const deviceToken = await getFCMToken();
        console.log('📱 [Auth Change] deviceToken:', deviceToken ? 'obtained' : 'null');

        if (deviceToken && authState.user?.id) {
          let deviceName = 'Web App';
          const ua = navigator.userAgent;
          
          let browser = '';
          if (/SamsungBrowser|Samsung Internet/i.test(ua)) {
            browser = 'Samsung Internet';
          } else if (/Edg/i.test(ua)) {
            browser = 'Edge';
          } else if (/Chrome/i.test(ua)) {
            browser = 'Chrome';
          } else if (/Firefox/i.test(ua)) {
            browser = 'Firefox';
          } else if (/iPhone|iPad|iPod/i.test(ua)) {
            browser = 'Safari';
          } else if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) {
            browser = 'Safari';
          }
          
          if (/iPhone|iPad|iPod/i.test(ua)) {
            deviceName = browser ? `iOS ${browser}` : 'iOS PWA';
          } else if (/Android/i.test(ua)) {
            deviceName = browser ? `Android ${browser}` : 'Android PWA';
          } else if (/Windows/i.test(ua)) {
            deviceName = browser ? `Windows ${browser}` : 'Windows PWA';
          } else if (/Mac/i.test(ua)) {
            deviceName = browser ? `macOS ${browser}` : 'macOS PWA';
          }

          console.log('📱 [Auth Change] Device detected as:', deviceName);
          await storeDeviceTokenViaFunction(authState.user.id, deviceToken, deviceName);
        }
      } catch (tokenError) {
        console.warn('⚠️ [Auth Change] Failed to capture device token:', tokenError);
      }
    })();
  }, [authState.isLoggedIn, authState.user?.id]);

  // Effect 4: Register Twilio VoIP on Android after login (for incoming call wake)
  useEffect(() => {
    if (!authState.isLoggedIn || !authState.user?.id) return;
    // Only a lead and above has the phone (staff are not phone agents), so only they register for incoming calls
    if (!PHONE_ROLES.includes(authState.user.role as Role)) return;
    registerTwilioVoip();
  }, [authState.isLoggedIn, authState.user?.id, authState.user?.role]);

  // Effect 5: Warm up cold-start-prone Cloud Functions right after login, well
  // before the user could reach any screen (Payments, Subscriptions, etc.) that
  // needs them for real — giving their containers real lead time to finish
  // provisioning in the background instead of blocking that screen's first load.
  useEffect(() => {
    if (!authState.isLoggedIn || !authState.user?.id) return;
    warmAllFunctions();
  }, [authState.isLoggedIn, authState.user?.id]);

  // Listen for automatic FCM token refresh when WNS reconnects (Windows fix)
  useEffect(() => {
    const handleTokenRefresh = async (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('🔔 ========================================');
      console.log('🔔 Automatic FCM token refresh triggered');
      console.log('🔔 Reason:', customEvent.detail?.reason);
      console.log('🔔 ========================================');

      // Only refresh if user is logged in
      if (!authState.isLoggedIn || !authState.user?.id) {
        console.warn('⚠️ Cannot refresh token: User not logged in');
        return;
      }

      try {
        // Get new FCM token
        const newToken = await getFCMToken();

        if (newToken) {
          console.log('✅ New FCM token obtained after WNS reconnect');

          // Detect platform and browser
          let deviceName = 'Web App';
          const ua = navigator.userAgent;
          
          // Detect browser first (more specific)
          let browser = '';
          if (/SamsungBrowser|Samsung Internet/i.test(ua)) {
            browser = 'Samsung Internet';
          } else if (/Edg/i.test(ua)) {
            browser = 'Edge';
          } else if (/Chrome/i.test(ua)) {
            browser = 'Chrome';
          } else if (/Firefox/i.test(ua)) {
            browser = 'Firefox';
          } else if (/iPhone|iPad|iPod/i.test(ua)) {
            // On iOS, all browsers use WebKit, so if not Chrome/Firefox/Edge, it's Safari
            browser = 'Safari';
          } else if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) {
            browser = 'Safari';
          }
          
          // Detect OS
          if (/iPhone|iPad|iPod/i.test(ua)) {
            deviceName = browser ? `iOS ${browser}` : 'iOS PWA';
          } else if (/Android/i.test(ua)) {
            deviceName = browser ? `Android ${browser}` : 'Android PWA';
          } else if (/Windows/i.test(ua)) {
            deviceName = browser ? `Windows ${browser}` : 'Windows PWA';
          } else if (/Mac/i.test(ua)) {
            deviceName = browser ? `macOS ${browser}` : 'macOS PWA';
          }

          console.log('📱 Device detected as:', deviceName);

          // Store new token
          await storeDeviceTokenViaFunction(authState.user.id, newToken, deviceName);
          console.log('✅ New FCM token stored successfully');
        } else {
          console.warn('⚠️ Failed to obtain new FCM token');
        }
      } catch (error) {
        console.error('❌ Error refreshing FCM token:', error);
      }
    };

    // Listen for custom event from service worker
    window.addEventListener('fcm-token-refresh-needed', handleTokenRefresh);

    return () => {
      window.removeEventListener('fcm-token-refresh-needed', handleTokenRefresh);
    };
  }, [authState.isLoggedIn, authState.user?.id]);

  // Login with Firebase ID token
  const login = useCallback(async (idToken: string): Promise<LoginResult> => {
    try {
      // Use rsAuthClient for token-based login (passkey)
      const authClient = new rsAuthClient();
      const result = await authClient.login(idToken);
      
      if (result.success && result.user) {
        // User object already has avatar from rsAuth function
        const enhancedUser = result.user;

        setAuthState({
          isLoggedIn: true,
          isLoading: false,
          user: enhancedUser,
          error: null
        });
        
        // Return the result with enhanced user
        return {
          ...result,
          user: enhancedUser
        };
      } else {
        setAuthState({
          isLoggedIn: false,
          isLoading: false,
          user: null,
          error: result.error || 'Login failed'
        });
        return result;
      }
    } catch (error) {
      setAuthState({
        isLoggedIn: false,
        isLoading: false,
        user: null,
        error: error instanceof Error ? error.message : t('contexts.AuthContext.unknownError')
      });
      return {
        success: false,
        userID: '',
        error: error instanceof Error ? error.message : t('contexts.AuthContext.unknownError')
      };
    }
  }, [t]);

  // Profile + role of the account that is already signed in (the login screen signs in with Firebase first)
  const loginBackend = useCallback(async (): Promise<LoginResult> => {
    try {
      // Use rsAuthClient loginBackend method
      const authClient = new rsAuthClient();
      const result = await authClient.loginBackend();
      
      if (result.success && result.user) {
        // User object already has avatar from rsAuth function
        const enhancedUser = result.user;
        
        // Firebase Auth already holds the session (the login screen signed in first)
        setAuthState({
          isLoggedIn: true,
          isLoading: false,
          user: enhancedUser,
          error: null
        }); // Allow auth state listener to resume
        
      } else {
        setAuthState({
          isLoggedIn: false,
          isLoading: false,
          user: null,
          error: result.error || 'Login failed'
        });
      }
      return result;
    } catch (error) {
      setAuthState({
        isLoggedIn: false,
        isLoading: false,
        user: null,
        error: error instanceof Error ? error.message : t('contexts.AuthContext.unknownError')
      });
      return {
        success: false,
        userID: '',
        error: error instanceof Error ? error.message : t('contexts.AuthContext.unknownError')
      };
    }
  }, [t]);

  // Backend register function - creates Firebase user on backend
  const registerBackend = useCallback(async (email: string, password: string, firstName?: string, lastName?: string, phone?: string): Promise<LoginResult> => {
    try {
      // Use rsAuthClient registerBackend method
      const authClient = new rsAuthClient();
      const result = await authClient.registerBackend(email, password, { firstName, lastName, phone });
      
      // For backend registration, we don't set user as logged in since they need to verify email first
      setAuthState({
        isLoggedIn: false,
        isLoading: false,
        user: null,
        error: result.success ? null : (result.error?.toString() || result.message?.toString() || 'Registration failed')
      });
      return result;
    } catch (error) {
      setAuthState({
        isLoggedIn: false,
        isLoading: false,
        user: null,
        error: error instanceof Error ? error.message : t('contexts.AuthContext.unknownError')
      });
      return {
        success: false,
        userID: '',
        error: error instanceof Error ? error.message : t('contexts.AuthContext.unknownError')
      };
    }
  }, [t]);

  // Resend email verification
  const resendVerification = useCallback(async (email: string): Promise<LoginResult> => {
    try {
      const authClient = new rsAuthClient();
      const result = await authClient.resendVerification(email);
      
      return result;
    } catch (error) {
      console.error('AuthProvider resendVerification error:', error);
      return {
        success: false,
        userID: '',
        error: error instanceof Error ? error.message : t('contexts.AuthContext.failedToResendVerificationEmail')
      };
    }
  }, [t]);

  // Verify email verification code
  const verifyEmailCode = useCallback(async (email: string, code: string): Promise<LoginResult> => {
    try {
      const authClient = new rsAuthClient();
      const result = await authClient.verifyEmailCode(email, code);
      
      return result;
    } catch (error) {
      console.error('AuthProvider verifyEmailCode error:', error);
      return {
        success: false,
        userID: '',
        error: error instanceof Error ? error.message : t('contexts.AuthContext.failedToVerifyEmailCode')
      };
    }
  }, [t]);

  // Verify employee role assignment code
  const verifyEmployeeCode = useCallback(async (email: string, code: string): Promise<LoginResult> => {
    try {
      const authClient = new rsAuthClient();
      const result = await authClient.verifyEmployeeCode(email, code);
      
      return result;
    } catch (error) {
      console.error('AuthProvider verifyEmployeeCode error:', error);
      return {
        success: false,
        userID: '',
        error: error instanceof Error ? error.message : t('contexts.AuthContext.failedToVerifyEmployeeCode')
      };
    }
  }, [t]);

  // Function to update user with fresh contact profile data from localStorage
  const updateUserWithStoredProfile = useCallback(async (): Promise<void> => {
    if (!authState.isLoggedIn || !authState.user) return;
    
    try {
      const storedProfile = localStorage.getItem('contactProfile') || localStorage.getItem('wixContactProfile');
      if (storedProfile) {
        const parsedProfile = JSON.parse(storedProfile);
        const contactProfileData = parsedProfile.profile ? parsedProfile : { profile: parsedProfile };
        
        const enhancedUser = {
          ...authState.user,
          // Use display name if available, otherwise nickname, otherwise keep existing name
          name: contactProfileData.profile?.displayName || contactProfileData.profile?.nickname || authState.user.name,
          // Extract first name from display name or nickname for firstName field
          firstName: (contactProfileData.profile?.displayName || contactProfileData.profile?.nickname || authState.user.name)?.split(' ')[0] || authState.user.firstName,
          // Store avatar photo if available
          ...(contactProfileData.profile?.avatar && { 
            photoURL: contactProfileData.profile.avatar,
            avatarUrl: contactProfileData.profile.avatar,
            profileImage: contactProfileData.profile.avatar
          }),
          // Store contact data in the existing profileData field
          profileData: {
            ...authState.user.profileData,
            contactId: contactProfileData.contact?._id || contactProfileData._id,
            loginEmail: contactProfileData.contact?.loginEmail || contactProfileData.loginEmail,
            profile: contactProfileData.profile
          }
        };
        
        setAuthState({
          ...authState,
          user: enhancedUser
        });
      }
    } catch (error) {
      console.warn('[AuthContext] Error updating user with stored profile:', error);
    }
  }, [authState]);

  const updateLocalPreferences = useCallback((preferences: { dashboardShortcuts?: string[] }) => {
    if (!authState.user) return;
    const updatedUser = {
      ...authState.user,
      preferences: { ...authState.user.preferences, ...preferences }
    };
    setAuthState(prev => ({ ...prev, user: updatedUser }));
    try {
      const sd = localStorage.getItem('session');
      if (sd) {
        const parsed = JSON.parse(sd);
        parsed.user = updatedUser;
        localStorage.setItem('session', JSON.stringify(parsed));
      }
    } catch {}
  }, [authState]);

  // Function to refresh user data from the server
  const refreshUserData = useCallback(async (): Promise<void> => {
    if (!authState.isLoggedIn) return;
    
    try {
      // Get current Firebase user and refresh token to call backend for fresh data
      const currentFirebaseUser = auth.currentUser;
      if (currentFirebaseUser) {
        // Get fresh ID token and call backend for updated user data  
        const idToken = await currentFirebaseUser.getIdToken(true); // force refresh
        
        const authClient = new rsAuthClient();
        const backendResult = await authClient.login(idToken);
        
        if (backendResult.success && backendResult.user) {
          // Merge fresh user data from server with existing user data
          const updatedUser = {
            ...authState.user,
            ...backendResult.user,
            // Ensure preferences are updated from server
            preferences: backendResult.user.preferences || authState.user?.preferences
          };
          
          setAuthState({
            ...authState,
            user: updatedUser
          });
        }
      }
    } catch (error) {
      console.warn('[AuthContext] Error refreshing user data:', error);
    }
  }, [authState]);

  // Logout function using Firebase Auth
  const logout = useCallback(async (): Promise<void> => {
    // Note: Device tokens are stored in Firebase and managed by the backend
    // On logout, the user's device tokens remain in Firebase but become inactive
    // The next login will generate a new token and store it
    
    // Clear session storage first
    const authService = DataServiceFactory.getService().auth;
    authService.logout();
    
    // SECURITY: Clear all cached profile data on logout
    localStorage.removeItem('contactProfile');
    localStorage.removeItem('wixContactProfile');
    
    // Then sign out from Firebase Auth
    await signOut(auth);
    
    setAuthState({
      isLoggedIn: false,
      isLoading: false,
      user: null,
      error: null
    });
  }, []);

  const actions = useMemo(() => ({
    login,
    loginBackend,
    registerBackend,
    resendVerification,
    verifyEmailCode,
    verifyEmployeeCode,
    logout,
    updateUserWithStoredProfile,
    refreshUserData,
    updateLocalPreferences
  }), [
    login,
    loginBackend,
    registerBackend,
    resendVerification,
    verifyEmailCode,
    verifyEmployeeCode,
    logout,
    updateUserWithStoredProfile,
    refreshUserData,
    updateLocalPreferences
  ]);

  const contextValue = useMemo(() => ({
    authState,
    actions,
    user: authState.user,
    notifications,
    unreadCount,
    markNotificationsSeen,
    categoryCounts
  }), [authState, actions, notifications, unreadCount, markNotificationsSeen, categoryCounts]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};